import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { useGetRestaurantQuery } from "../../hooks/useGetRestaurantQuery";
import { FullScreenSpinner } from "../../tabin/components/fullScreenSpinner";
import {
    convertCentsToDollars,
    getQuantityRemainingText,
    isProductQuantityAvailable,
    isOrderTypeAllowed,
    isItemAvailable,
    isItemSoldOut,
} from "../../util/util";
import { ProductModal } from "../modals/product";
import { IGET_RESTAURANT_PRODUCT, IGET_RESTAURANT_CATEGORY, IS3Object, EOrderType, GET_URL_REDIRECT_BY_ORIGINAL_URL } from "../../graphql/customQueries";
import { useCart } from "../../context/cart-context";
import { PageWrapper } from "../../tabin/components/pageWrapper";
// import { ItemAddedUpdatedModal } from "../modals/itemAddedUpdatedModal";
import { ICartProduct } from "../../model/model";
import { getCloudFrontDomainName } from "../../private/aws-custom";
import { useRestaurant } from "../../context/restaurant-context";

import { CachedImage } from "../../tabin/components/cachedImage";
import { useRegister } from "../../context/register-context";
import { Checkout } from "./checkout";
import { isMobile } from "react-device-detect";
import { Button } from "../../tabin/components/button";
import { Input } from "../../tabin/components/input";
import { checkoutPath, configureQRRedirectPath } from "../main";
import { useLocation, useNavigate, useParams } from "react-router";
import { OrderTypeModal } from "../modals/orderTypeModal";
import { useQuery } from "@apollo/client";
import { IoClose, IoSearch } from "react-icons/io5";

import "./restaurant.scss";

interface IMenuProduct {
    category: IGET_RESTAURANT_CATEGORY;
    product: IGET_RESTAURANT_PRODUCT;
}

const sortAvailableItemsFirst = <T,>(items: readonly T[], isAvailable: (item: T) => boolean): T[] => {
    const available: T[] = [];
    const unavailable: T[] = [];

    items.forEach((item) => {
        (isAvailable(item) ? available : unavailable).push(item);
    });

    return [...available, ...unavailable];
};

const isMenuCategoryAvailable = (category: IGET_RESTAURANT_CATEGORY) =>
    !isItemSoldOut(category.soldOut, category.soldOutDate) && isItemAvailable(category.availability);

export const Restaurant = () => {
    const location = useLocation();

    const { restaurantId, selectedCategoryId, selectedProductId } = useParams();

    const queryParams = new URLSearchParams(location.search);
    const table = queryParams.get("table");

    const { setTableNumber, orderType } = useCart();

    setTableNumber(table);

    // context
    const { cartProductQuantitiesById, addProduct, setProducts, products, subTotal } = useCart();
    const { register, selectRegister } = useRegister();
    const { setRestaurant, menuCategories: restaurantCategories, menuProducts: restaurantProducts } = useRestaurant();

    const navigate = useNavigate();
    const isValidQRCodeLength = !!restaurantId && restaurantId.length === 6;
    const [handledRedirect, setHandledRedirect] = useState(false);

    const { data: redirectData, loading: redirectLoading } = useQuery(GET_URL_REDIRECT_BY_ORIGINAL_URL, {
        variables: {
            originalUrl: window.location.href || "",
            limit: 1,
        },
        skip: !isValidQRCodeLength,
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        // This will need to be removed once Naumi Wellington does their fix on their end.
        if (restaurantId === "37742822-8acf-402d-9da2-c2e36470f6c4") {
            // Construct the new URL with query parameters
            const newUrl = `https://lolarouge-backup.tabin.co.nz/37742822-8acf-402d-9da2-c2e36470f6c4/?table=${table}`;
            window.location.href = newUrl;
        }
    }, []);

    useEffect(() => {
        if (!isValidQRCodeLength || handledRedirect) return;
        if (redirectLoading) return;

        const redirectUrl = redirectData?.getUrlRedirectByOriginalUrl?.items?.[0]?.redirectUrl;

        if (redirectUrl) {
            setHandledRedirect(true);
            window.location.href = redirectUrl;
            return;
        }

        setHandledRedirect(true);
        navigate(configureQRRedirectPath, { state: { from: window.location.href } });
    }, [isValidQRCodeLength, handledRedirect, redirectLoading, redirectData, navigate]);

    // query
    const { data: restaurant, error: getRestaurantError, loading: getRestaurantLoading } = useGetRestaurantQuery(restaurantId || "", isValidQRCodeLength);

    // states
    const [selectedCategory, setSelectedCategory] = useState<IGET_RESTAURANT_CATEGORY | null>(null);
    //selectedCategoryForProductModal is a different one so that when you either search for a product or select a most popular product it does not change the selectedCategory.
    const [selectedCategoryForProductModal, setSelectedCategoryForProductModal] = useState<IGET_RESTAURANT_CATEGORY | null>(null);
    const [selectedProductForProductModal, setSelectedProductForProductModal] = useState<IGET_RESTAURANT_PRODUCT | null>(null);
    const [showProductModal, setShowProductModal] = useState(false);
    // const [showItemAddedModal, setShowItemAddedModal] = useState(false);
    const [showSearchField, setShowSearchField] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [showOrderTypeModal, setShowOrderTypeModal] = useState(false);

    const [mostPopularProducts, setMostPopularProducts] = useState<IMenuProduct[]>([]);
    const [subCategories, setSubCategories] = useState<string[]>([]);

    const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>();

    const searchButtonRef = useRef<HTMLButtonElement>(null);

    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const isSearching = showSearchField && normalizedSearchTerm.length > 0;
    const searchResults = useMemo(() => {
        if (!isSearching || !restaurant || !register) return [];

        const matches: IMenuProduct[] = [];
        const seenProductIds = new Set<string>();

        restaurant.categories.items.forEach((category) => {
            if (category.availablePlatforms && !category.availablePlatforms.includes(register.type)) return;
            if (!isOrderTypeAllowed(orderType, category.availableOrderTypes)) return;
            if (!isItemAvailable(category.availability) || isItemSoldOut(category.soldOut, category.soldOutDate)) return;

            category.products?.items.forEach(({ product }) => {
                if (product.availablePlatforms && !product.availablePlatforms.includes(register.type)) return;
                if (!isOrderTypeAllowed(orderType, product.availableOrderTypes)) return;
                if (!product.name.toLowerCase().includes(normalizedSearchTerm) || seenProductIds.has(product.id)) return;

                seenProductIds.add(product.id);
                matches.push({ category, product });
            });
        });

        return matches;
    }, [isSearching, normalizedSearchTerm, restaurant, register, orderType]);

    useEffect(() => {
        if (!restaurant) return;

        // if (restaurant.enableOnlineDelivery) setShowOrderTypeModal(true);
        setShowOrderTypeModal(true);
    }, [restaurant]);

    useEffect(() => {
        if (restaurant) {
            setRestaurant(restaurant);

            restaurant.registers.items.forEach((r) => {
                if (r.type == "ONLINE") {
                    selectRegister(r);
                }
            });
        }
    }, [restaurant]);

    useEffect(() => {
        if (restaurant) {
            if (selectedCategoryId) {
                const selectedCategoryItem = restaurantCategories[selectedCategoryId];

                if (selectedCategoryItem && isOrderTypeAllowed(orderType, selectedCategoryItem.availableOrderTypes)) {
                    setSelectedCategory(selectedCategoryItem);
                    onProcessSubCategories(selectedCategoryItem);
                }
            } else if (register && register.defaultCategoryView) {
                const selectedCategoryItem = restaurantCategories[register.defaultCategoryView];

                if (selectedCategoryItem && isOrderTypeAllowed(orderType, selectedCategoryItem.availableOrderTypes)) {
                    setSelectedCategory(selectedCategoryItem);
                    onProcessSubCategories(selectedCategoryItem);
                }
            } else {
                for (const selectedCategoryItem of restaurant.categories.items) {
                    const isSoldOut = isItemSoldOut(selectedCategoryItem.soldOut, selectedCategoryItem.soldOutDate);
                    const isAvailable = isItemAvailable(selectedCategoryItem.availability);

                    const isValid = !isSoldOut && isAvailable && isOrderTypeAllowed(orderType, selectedCategoryItem.availableOrderTypes);

                    if (isValid) {
                        setSelectedCategory(selectedCategoryItem);
                        onProcessSubCategories(selectedCategoryItem);
                        break;
                    }
                }
            }

            if (restaurantProducts && register && register.preSelectedProducts) {
                const cartProducts: ICartProduct[] = [];

                register.preSelectedProducts.forEach((preSelectedProduct) => {
                    const product = restaurantProducts[preSelectedProduct];

                    if (product && isOrderTypeAllowed(orderType, product.availableOrderTypes)) {
                        if (!product.categories.items[0]) return;
                        if (!product.categories.items[0].category || !isOrderTypeAllowed(orderType, product.categories.items[0].category.availableOrderTypes))
                            return;

                        const productToOrder: ICartProduct = {
                            isPreSelectedProduct: true,
                            id: product.id,
                            name: product.name,
                            kitchenName: product.kitchenName,
                            price: orderType === EOrderType.DELIVERY && product.deliveryPrice ? product.deliveryPrice : product.price,
                            totalPrice: orderType === EOrderType.DELIVERY && product.deliveryPrice ? product.deliveryPrice : product.price,
                            discount: 0,
                            reportingGroup: product.reportingGroup,
                            image: product.image
                                ? {
                                      key: product.image.key,
                                      region: product.image.region,
                                      bucket: product.image.bucket,
                                      identityPoolId: product.image.identityPoolId,
                                  }
                                : null,
                            quantity: 1,
                            notes: null,
                            category: {
                                id: product.categories.items[0].category.id,
                                name: product.categories.items[0].category.name,
                                kitchenName: product.categories.items[0].category.kitchenName,
                                image: product.categories.items[0].category.image
                                    ? {
                                          key: product.categories.items[0].category.image.key,
                                          region: product.categories.items[0].category.image.region,
                                          bucket: product.categories.items[0].category.image.bucket,
                                          identityPoolId: product.categories.items[0].category.image.identityPoolId,
                                      }
                                    : null,
                            },
                            modifierGroups: [],
                        };

                        cartProducts.push(productToOrder);
                    }
                });

                if (cartProducts) setProducts(cartProducts);
            }

            // if (selectedProductId) {
            //     const selectedProductItem = restaurantProducts[selectedProductId];

            //     if (selectedProductItem) {
            //         setSelectedProductForProductModal(selectedProductItem);
            //         setSelectedCategoryForProductModal(selectedProductItem.categories.items[0]?.category || null);
            //         setShowProductModal(true);
            //     }
            // }
        }
    }, [restaurant, restaurantCategories, restaurantProducts, register, orderType, selectedCategoryId, selectedProductId]);

    const onProcessSubCategories = (category: IGET_RESTAURANT_CATEGORY) => {
        const newSubCategories: string[] = [];

        category.products &&
            category.products.items.forEach((p) => {
                p.product.subCategories &&
                    p.product.subCategories.split(";").forEach((subCategory) => {
                        if (!newSubCategories.includes(subCategory)) newSubCategories.push(subCategory);
                    });
            });

        setSubCategories(newSubCategories);

        if (newSubCategories.length > 0) {
            setSelectedSubCategory(newSubCategories[0]);
        } else {
            setSelectedSubCategory(null);
        }
    };

    // useEffect(() => {
    //     if (restaurant) {
    //         setRestaurant(restaurant);

    //         // restaurant.categories.items.every((c) => {
    //         //     if (isItemAvailable(c.availability)) {
    //         //         setSelectedCategory(c);
    //         //         return false;
    //         //     }
    //         //     return true;
    //         // });
    //     }
    // }, [restaurant]);

    const compareSortFunc = (a: IMenuProduct, b: IMenuProduct) => {
        if (!a.product.totalQuantitySold || !b.product.totalQuantitySold) return 0;
        if (a.product.totalQuantitySold > b.product.totalQuantitySold) return -1;
        if (a.product.totalQuantitySold < b.product.totalQuantitySold) return 1;
        return 0;
    };

    useEffect(() => {
        if (!restaurant || !register) return;

        const newMostPopularProducts: IMenuProduct[] = [];

        restaurant.categories.items.forEach((c) => {
            if (c.availablePlatforms && !c.availablePlatforms.includes(register.type)) return;
            if (!isOrderTypeAllowed(orderType, c.availableOrderTypes)) return;

            c.products &&
                c.products.items.forEach((p) => {
                    if (p.product.availablePlatforms && !p.product.availablePlatforms.includes(register.type)) return;
                    if (!isOrderTypeAllowed(orderType, p.product.availableOrderTypes)) return;

                    if (p.product.totalQuantitySold) {
                        newMostPopularProducts.push({
                            category: c,
                            product: p.product,
                        });
                    }
                });
        });

        newMostPopularProducts.sort(compareSortFunc);
        setMostPopularProducts(newMostPopularProducts.slice(0, 20));
    }, [restaurant, register, orderType]);

    useEffect(() => {
        if (!selectedCategory) return;
        if (isOrderTypeAllowed(orderType, selectedCategory.availableOrderTypes)) return;

        setSelectedCategory(null);
    }, [selectedCategory, orderType]);

    useEffect(() => {
        if (showProductModal) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
    }, [showProductModal]);

    // displays THAT SHOULD BE IN CONTEXT
    if (isValidQRCodeLength) {
        if (redirectLoading || !handledRedirect) {
            return <FullScreenSpinner show={true} text="Loading QR code..." />;
        }

        return <FullScreenSpinner show={true} text="Loading restaurant..." />;
    }

    if (getRestaurantLoading) {
        return <FullScreenSpinner show={true} text="Loading restaurant" />;
    }

    if (getRestaurantError) {
        return <h1>Couldn't get restaurant. Try Refreshing, {getRestaurantError.message}</h1>;
    }

    if (!restaurant) {
        return <div>Restaurant does not exist</div>;
    }

    if (!restaurant.verified) {
        return <div>Restaurant is not verified</div>;
    }

    if (!register) {
        return <div>This restaurant has not been set up for online ordering!</div>;
    }

    // callbacks
    const onCloseProductModal = () => {
        setShowProductModal(false);
    };

    const onCloseOrderTypeModal = () => {
        setShowOrderTypeModal(false);
    };

    const onAddProduct = (product: ICartProduct) => {
        addProduct(product);
        // setShowItemAddedModal(true);
    };

    // const onCloseItemAddedModal = () => {
    //     setShowItemAddedModal(false);
    // };

    const onCloseSearch = () => {
        setShowSearchField(false);
        setSearchTerm("");
        searchButtonRef.current?.focus();
    };

    const onToggleSearch = () => {
        if (showSearchField) {
            onCloseSearch();
        } else {
            setShowSearchField(true);
        }
    };

    const onChangeSearchTerm = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const onSearchKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape") {
            event.preventDefault();
            onCloseSearch();
        }
    };

    const onClickProduct = (category: IGET_RESTAURANT_CATEGORY, product: IGET_RESTAURANT_PRODUCT) => {
        setSelectedCategoryForProductModal(category);
        setSelectedProductForProductModal(product);
        setShowProductModal(true);
    };

    const productModal = (
        <>
            {selectedCategoryForProductModal && selectedProductForProductModal && showProductModal && (
                <ProductModal
                    isOpen={showProductModal}
                    category={selectedCategoryForProductModal}
                    product={selectedProductForProductModal}
                    onAddProduct={onAddProduct}
                    onClose={onCloseProductModal}
                />
            )}
        </>
    );

    // const itemAddedModal = (
    //     <>{showItemAddedModal && <ItemAddedUpdatedModal isOpen={showItemAddedModal} onClose={onCloseItemAddedModal} isProductUpdate={false} />}</>
    // );

    const orderTypeModal = <>{showOrderTypeModal && <OrderTypeModal isOpen={showOrderTypeModal} onClose={onCloseOrderTypeModal} />}</>;

    const modals = (
        <>
            {productModal}
            {/* {itemAddedModal} */}
            {orderTypeModal}
        </>
    );

    const isMenuProductAvailable = (category: IGET_RESTAURANT_CATEGORY, product: IGET_RESTAURANT_PRODUCT) =>
        !isItemSoldOut(product.soldOut, product.soldOutDate) &&
        isItemAvailable(product.availability) &&
        isMenuCategoryAvailable(category) &&
        isProductQuantityAvailable(product, cartProductQuantitiesById);

    const ProductDisplay = (category: IGET_RESTAURANT_CATEGORY, product: IGET_RESTAURANT_PRODUCT) => {
        const isValid = isMenuProductAvailable(category, product);

        const addToCart = products && products.find((item) => item.id === product.id);

        return (
            <>
                <div
                    key={product.id}
                    className={`product ${isValid ? "" : "sold-out"} ${addToCart ? "add-to-cart" : ""}`}
                    onClick={() => isValid && onClickProduct(category, product)}
                >
                    <div className="product-quantity">{addToCart && addToCart.quantity}</div>
                    {product.totalQuantityAvailable && product.totalQuantityAvailable <= 5 ? (
                        <span className="quantity-remaining ml-2">{getQuantityRemainingText(product.totalQuantityAvailable)}</span>
                    ) : (
                        <></>
                    )}

                    {product.imageUrl ? (
                        <CachedImage url={`${product.imageUrl}`} className="image mb-1" alt="product-image" />
                    ) : product.image ? (
                        <>
                            <CachedImage
                                className="image mb-1"
                                url={`${getCloudFrontDomainName()}/protected/${product.image.identityPoolId}/${product.image.key}`}
                                alt="product-image"
                            />
                        </>
                    ) : null}

                    <div className="name text-bold">{isValid ? `${product.name}` : `${product.name} (SOLD OUT)`}</div>

                    {product.description && <div className="description mt-1">{product.description}</div>}

                    {product.tags && (
                        <div className="tags mt-1">
                            {product.tags.split(";").map((tag) => (
                                <div className="tag">{tag}</div>
                            ))}
                        </div>
                    )}

                    <div className={`display-price mt-1 ${product.displayPrice ? "" : "display-none"}`}>{product.displayPrice}</div>
                    <div className={`price mt-1 ${product.displayPrice ? "display-none" : ""}`}>
                        ${convertCentsToDollars(orderType === EOrderType.DELIVERY && product.deliveryPrice ? product.deliveryPrice : product.price)}
                    </div>
                </div>
            </>
        );
    };

    const Category = (props: { isSelected: boolean; category: IGET_RESTAURANT_CATEGORY; onCategorySelected: (category: IGET_RESTAURANT_CATEGORY) => void }) => {
        const { isSelected, category, onCategorySelected } = props;

        const isValid = isMenuCategoryAvailable(category);

        return (
            <div
                key={category.id}
                className={`category ${isSelected ? "selected" : ""} ${isValid ? "" : "sold-out"}`}
                onClick={() => {
                    isValid && onCategorySelected(category);
                }}
            >
                {!isValid ? (
                    <div className={`name`}>{category.name} (UNAVAILABLE)</div>
                ) : isSelected ? (
                    <div className="name text-bold">{category.name}</div>
                ) : (
                    <div className="name">{category.name}</div>
                )}
            </div>
        );
    };

    const menuCategories = (
        <>
            {sortAvailableItemsFirst(restaurant.categories.items, isMenuCategoryAvailable).map((c) => {
                if (c.availablePlatforms && !c.availablePlatforms.includes(register.type)) return;
                if (!isOrderTypeAllowed(orderType, c.availableOrderTypes)) return;

                return (
                    <Category
                        key={c.id}
                        isSelected={selectedCategory != null && selectedCategory.id == c.id}
                        category={c}
                        onCategorySelected={(category: IGET_RESTAURANT_CATEGORY) => {
                            setSelectedCategory(category);
                            onProcessSubCategories(category);
                        }}
                    />
                );
            })}
        </>
    );

    const menuSearchField = (
        <>
            {showSearchField && (
                <div id="restaurant-menu-search" className="restaurant-menu-search" role="search" aria-label="Search menu" onKeyDown={onSearchKeyDown}>
                    <IoSearch size={20} aria-hidden="true" focusable="false" />
                    <Input
                        className="product-search-field"
                        name="search"
                        type="search"
                        placeholder="Search products..."
                        autoFocus
                        value={searchTerm}
                        onChange={onChangeSearchTerm}
                    />
                </div>
            )}
        </>
    );

    const menuSearchButton = (
        <button
            ref={searchButtonRef}
            type="button"
            className="restaurant-search-button category"
            aria-label={showSearchField ? "Close search" : "Search menu"}
            title={showSearchField ? "Close search" : "Search menu"}
            aria-expanded={showSearchField}
            aria-controls={showSearchField ? "restaurant-menu-search" : undefined}
            onClick={onToggleSearch}
        >
            {showSearchField ? <IoClose size={22} aria-hidden="true" focusable="false" /> : <IoSearch size={22} aria-hidden="true" focusable="false" />}
        </button>
    );

    const menuMostPopularCategory = (
        <div
            className={`category background-grey ${!selectedCategory ? "selected" : ""}`}
            onClick={() => {
                setSelectedCategory(null);
            }}
        >
            {/* <CachedImage className="icon" url={`${getPublicCloudFrontDomainName()}/images/most-popular.png`} alt="most-popular-icon" /> */}
            <div className="name">Most Popular</div>
        </div>
    );

    const menuMostPopularProducts = (
        <div>
            {!selectedCategory && (
                <>
                    <div className="product-category-name h1 text-center">Most Popular</div>
                    <div className="products">
                        {sortAvailableItemsFirst(mostPopularProducts, ({ category, product }) => isMenuProductAvailable(category, product)).map(
                            ({ category, product }) => ProductDisplay(category, product),
                        )}
                    </div>
                </>
            )}
        </div>
    );

    const menuProducts = (
        <div>
            {selectedCategory &&
                restaurant.categories.items.map((c) => {
                    if (selectedCategory.id !== c.id) return;
                    if (c.availablePlatforms && !c.availablePlatforms.includes(register.type)) return;
                    if (!isOrderTypeAllowed(orderType, c.availableOrderTypes)) return;

                    return (
                        <>
                            <div className="product-category-name h1 text-center mb-6">{c.name}</div>
                            {c.description && <div className="product-category-description text-bold text-center mb-6 h3">{c.description}</div>}
                            {subCategories.length > 0 && (
                                <div className="product-sub-category-wrapper mb-6">
                                    {subCategories.map((subCategory) => (
                                        <div
                                            className={`product-sub-category h3 ${selectedSubCategory === subCategory ? "selected" : ""}`}
                                            onClick={() => setSelectedSubCategory(subCategory)}
                                        >
                                            {subCategory}
                                        </div>
                                    ))}
                                    <div
                                        className={`product-sub-category background-grey h3 ${selectedSubCategory === null ? "selected" : ""}`}
                                        onClick={() => setSelectedSubCategory(null)}
                                    >
                                        All
                                    </div>
                                </div>
                            )}
                            {/* {c.image && (
                                <div className="product-category-image-wrapper mb-6">
                                    <CachedImage
                                        url={`${getCloudFrontDomainName()}/protected/${c.image.identityPoolId}/${c.image.key}`}
                                        className="product-category-image"
                                        alt="category-image"
                                    />
                                </div>
                            )} */}
                            <div className="products">
                                {c.products &&
                                    sortAvailableItemsFirst(c.products.items, ({ product }) => isMenuProductAvailable(c, product)).map((p) => {
                                        if (p.product.availablePlatforms && !p.product.availablePlatforms.includes(register.type)) return;
                                        if (!isOrderTypeAllowed(orderType, p.product.availableOrderTypes)) return;
                                        if (
                                            (selectedSubCategory &&
                                                p.product.subCategories &&
                                                !p.product.subCategories.split(";").includes(selectedSubCategory)) ||
                                            (selectedSubCategory && selectedSubCategory && !p.product.subCategories)
                                        )
                                            return;

                                        return ProductDisplay(c, p.product);
                                    })}
                            </div>
                        </>
                    );
                })}
        </div>
    );

    const menuSearchResults = (
        <div>
            <div className="product-category-name h1 text-center">Search results</div>
            {searchResults.length === 0 && (
                <div className="mb-4 text-center" role="status">
                    No products found. Try another search.
                </div>
            )}
            <div className="products">
                {sortAvailableItemsFirst(searchResults, ({ category, product }) => isMenuProductAvailable(category, product)).map(({ category, product }) => (
                    <Fragment key={product.id}>{ProductDisplay(category, product)}</Fragment>
                ))}
            </div>
        </div>
    );

    const checkout = (
        <>
            <div className="checkout">
                <Checkout />
            </div>
        </>
    );

    const onClickCart = () => {
        navigate(checkoutPath);
    };

    const restaurantFooter = (
        <>
            <div className="total-container">
                <div className="total-wrapper">
                    {/* <CachedImage
                        className="shopping-bag-icon mr-2"
                        url={`${getPublicCloudFrontDomainName()}/images/shopping-bag-icon.png`}
                        alt="shopping-bag-icon"
                    /> */}
                    <div className="h4 total">Total: ${convertCentsToDollars(subTotal)}</div>
                </div>
                <Button className="view-my-order-button" disabled={!products || products.length == 0} onClick={onClickCart}>
                    View My Order
                </Button>
            </div>
            {/* <Button className="cancel-button" onClick={onCancelOrder}>
                Cancel Order
            </Button> */}
        </>
    );

    return (
        <>
            <PageWrapper>
                <div className="restaurant">
                    <div className="restaurant-container">
                        {restaurant.logo && <RestaurantLogo image={restaurant.logo} />}
                        <div className="categories-wrapper" role="region" aria-label="Menu categories" tabIndex={0} hidden={showSearchField}>
                            {/* {restaurant.logo && <RestaurantLogo image={restaurant.logo} />} */}
                            {register.hideMostPopularCategory ? <></> : menuMostPopularCategory}
                            {menuCategories}
                        </div>
                        {menuSearchField}
                        {menuSearchButton}
                        <div className="products-wrapper">
                            {isSearching ? (
                                menuSearchResults
                            ) : (
                                <>
                                    {menuMostPopularProducts}
                                    {menuProducts}
                                </>
                            )}
                        </div>
                    </div>
                    {/* {isMobile && products && products.length > 0 && <div className="footer-wrapper">{restaurantFooter}</div>} */}
                    {isMobile && <div className="footer-wrapper">{restaurantFooter}</div>}

                    {!isMobile && <div className="checkout-container">{checkout}</div>}
                </div>
                {modals}
            </PageWrapper>
        </>
    );
};

const RestaurantLogo = (props: { image?: IS3Object | null }) => {
    return (
        <div className="restaurant-logo-wrapper">
            {props.image && (
                <CachedImage
                    url={`${getCloudFrontDomainName()}/protected/${props.image.identityPoolId}/${props.image.key}`}
                    className="restaurant-logo"
                    alt="restaurant-logo"
                />
            )}
        </div>
    );
};
